import React, { useState, useEffect, useRef, useCallback, FC } from "react";
import { Device, Facilities, FacilityLayoutConfiguration } from "@app/shared";
import { useFacilityCenter } from "../../../../../helpers";
import { MapShape, OmniGoogleMap, OmniMapMethods } from "../../../../shared/maps";
import { Icons, LoadingIndicator } from "../../../../shared";
import { FovDeviceConfig } from "../../../../shared/LayoutDesigner/shapeConfigs/FovDeviceConfig";
import { useFetch } from "../../../../../hooks";

interface DeviceMappingStepProps {
    facilityId: string;
    deviceId: string;
    onNext: () => void;
    onBack: () => void;
}

export const DeviceMappingStep: FC<DeviceMappingStepProps> = ({ facilityId, deviceId, onNext, onBack }) => {
    const [device, setDevice] = useState<Device | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isDevicePositioned, setIsDevicePositioned] = useState(false);
    const [facility, , helpers] = useFetch(() => Facilities.getById(facilityId));

    const [facilityCenter, isCenterLoading] = useFacilityCenter(facility);
    const map = useRef<OmniMapMethods>(null);

    useEffect(() => {
        const loadDevice = async () => {
            try {
                let foundDevice = facility?.devices?.find((d) => d.id === deviceId);

                if (!foundDevice) {
                    foundDevice = new Device({
                        id: deviceId,
                        facilityId: facility?.id,
                        name: `Device ${deviceId}`,
                        positionX: facility?.originReferencePointLatLng?.origin?.x || 0,
                        positionY: facility?.originReferencePointLatLng?.origin?.y || 0,
                        color: "#FF0000",
                        headingDegrees: 0,
                        desiredTiltDegrees: 0,
                        height: 3,
                    } as any);
                }

                setDevice(foundDevice);
                checkDevicePositioned(foundDevice);
            } catch (error) {
                console.error("Error loading device:", error);
            }
        };

        if (facility?.devices?.length && deviceId) {
            loadDevice();
        }
    }, [facility, deviceId]);

    const checkDevicePositioned = (currentDevice: Device) => {
        const defaultX = facility?.originReferencePointLatLng?.origin?.x || 0;
        const defaultY = facility?.originReferencePointLatLng?.origin?.y || 0;

        const isPositioned = currentDevice.positionX !== defaultX || currentDevice.positionY !== defaultY;
        setIsDevicePositioned(isPositioned);
    };

    const handleDeviceChange = useCallback(
        (updatedDevice: Device) => {
            setDevice(updatedDevice);
            setHasUnsavedChanges(true);
            checkDevicePositioned(updatedDevice);
        },
        [facility]
    );

    const handleSaveDevice = async () => {
        if (!device) return;

        try {
            setIsSaving(true);
            const layoutConfig = new FacilityLayoutConfiguration();
            layoutConfig.screenLines = facility?.screenLines || [];
            layoutConfig.detectionZones = facility?.detectionZones || [];
            layoutConfig.laneLines = facility?.laneLines || [];

            const existingDevices = facility?.devices || [];
            layoutConfig.devices = [...existingDevices.filter((d) => d.id !== device.id), device];

            layoutConfig.referencePoint = facility?.originReferencePointLatLng;

            await Facilities.updateLayoutConfiguration(facility?.id!, layoutConfig);
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error("Error saving device:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleContinue = async () => {
        if (!isDevicePositioned) {
            alert("Please position your device on the map before continuing.");
            return;
        }

        if (hasUnsavedChanges) {
            await handleSaveDevice();
        }
        onNext();
    };

    const centerMap = () => {
        map.current?.reCenter();
    };

    if (helpers.isLoading || isCenterLoading || !facilityCenter || !device || !facility) {
        return (
            <div className="flex justify-center items-center h-96">
                <LoadingIndicator isLoading={true} />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4">
            <div className="mb-8">
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center shadow-lg">
                        <Icons.LocationOutline className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold text-neutral-900">Position Your Device</h2>
                        <p className="text-neutral-600 mt-1">Drag the device marker on the map to match its real-world location, then configure its settings.</p>
                    </div>
                </div>

                <div className="flex gap-4 mb-4">
                    {hasUnsavedChanges && (
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl px-4 py-3 flex items-center gap-3">
                            <div className="w-5 h-5 bg-amber-500 rounded-lg flex items-center justify-center">
                                <Icons.AlertTriangle className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-sm font-medium text-amber-800">You have unsaved changes</span>
                        </div>
                    )}

                    {isDevicePositioned && (
                        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-300 rounded-xl px-4 py-3 flex items-center gap-3">
                            <div className="w-5 h-5 bg-emerald-500 rounded-lg flex items-center justify-center">
                                <Icons.Success className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-sm font-medium text-emerald-800">Device positioned successfully</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col xl:flex-row gap-8">
                <div className="flex-1 xl:flex-[2]">
                    <div className="bg-white rounded-2xl shadow-lg border border-warmGray-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-warmGray-100 bg-gradient-to-r from-warmGray-50 to-white">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-neutral-900">Device Location</h3>
                                    <p className="text-sm text-neutral-600 mt-1">Drag the red marker to position your device</p>
                                </div>
                                <button onClick={centerMap} className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-warmGray-50 rounded-xl text-sm font-medium text-neutral-700 border border-warmGray-200 hover:border-warmGray-300 transition-all duration-200 shadow-sm">
                                    <Icons.Target className="w-4 h-4" />
                                    Center Map
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="h-[500px] rounded-xl overflow-hidden border border-warmGray-200 shadow-inner">
                                <OmniGoogleMap center={facilityCenter} fitToPolygons={false} ref={map} draggable={true}>
                                    {device && <MapShape key={device.id} shape={device} facility={facility} isSelected={true} isEditable={true} onUpdate={(updatedDevice) => handleDeviceChange(updatedDevice as Device)} />}
                                </OmniGoogleMap>
                            </div>

                            <div className="mt-6 bg-gradient-to-r from-terracotta-50 to-warmGray-50 border border-brand-primary border-opacity-20 rounded-xl p-4">
                                <h4 className="text-sm font-semibold mb-3 text-neutral-900 flex items-center">
                                    <div className="w-5 h-5 bg-brand-primary rounded-lg flex items-center justify-center mr-2">
                                        <Icons.Info className="w-3 h-3 text-white" />
                                    </div>
                                    Instructions:
                                </h4>
                                <ul className="text-sm space-y-2 text-neutral-600">
                                    <li className="flex items-start gap-3">
                                        <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                                        Drag the device marker to position it at the physical device location
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                                        Use the configuration panel to set device orientation and properties
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="xl:w-96">
                    <div className="bg-white rounded-2xl shadow-lg border border-warmGray-100 overflow-hidden h-fit">
                        <div className="px-6 py-4 border-b border-warmGray-100 bg-gradient-to-r from-warmGray-50 to-white">
                            <h3 className="text-xl font-semibold text-neutral-900">Device Configuration</h3>
                            <p className="text-sm text-neutral-600 mt-1">Configure your device settings below</p>
                        </div>

                        <div className="p-6">
                            <div className="space-y-6">
                                <div className="rounded-xl border border-warmGray-200 p-5 bg-gradient-to-r from-warmGray-50 to-white">
                                    <h4 className="font-semibold text-neutral-900 mb-4 flex items-center gap-3">
                                        <div className="w-4 h-4 bg-brand-primary rounded-full border-2 border-white shadow-sm" />
                                        <span>{device.name || `Device ${deviceId}`}</span>
                                    </h4>

                                    <FovDeviceConfig facility={facility} device={device} availableDevices={[]} onChange={handleDeviceChange} isCollapsed={false} isEditable={true} />
                                </div>

                                <button onClick={handleSaveDevice} disabled={!hasUnsavedChanges || isSaving} className="w-full px-6 py-4 rounded-xl flex items-center justify-center gap-3 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none bg-brand-primary hover:bg-brand-primary-darkened text-white disabled:bg-warmGray-400 disabled:text-warmGray-200">
                                    {isSaving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Icons.Save className="w-5 h-5" />
                                            Save Device Position
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-between items-center mt-12 pt-8 border-t border-warmGray-200">
                <button onClick={onBack} className="flex items-center gap-3 px-6 py-3 bg-white hover:bg-warmGray-50 border border-warmGray-300 hover:border-warmGray-400 rounded-xl transition-all duration-200 font-semibold text-neutral-700">
                    <Icons.ArrowBack className="w-5 h-5" />
                    Back
                </button>

                <button onClick={handleContinue} disabled={!isDevicePositioned} className="flex items-center gap-3 px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none bg-brand-primary hover:bg-brand-primary-darkened text-white disabled:bg-warmGray-400 disabled:text-warmGray-200">
                    Continue to Mapping
                    <Icons.ArrowRight className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
};
